/** @jsxImportSource solid-js */

import {
  Component,
  Show,
  For,
  createSignal,
  createEffect,
  createMemo,
  onMount,
  batch,
  on,
} from 'solid-js';
import { ITab, IFilesFromComputerProps } from './types';
import { defineSolidIconButton } from '@tcweb/connect-ui';
import { translate } from '@fulcrum/lib';

interface IFilesFromComputerState {
  readonly selectedObject: File[];
  readonly type: ITab;
  readonly source: string;
}

interface IFilesFromComputerComponentProps {
  readonly selectedObject: File[];
  readonly isDragEnter: boolean;
  onDrop(e: DragEvent): void;
  onDragEnter(e: DragEvent): void;
  onDragOver(e: DragEvent): void;
  onDragLeave(): void;
  setFileUploadRef(el: HTMLInputElement): void;
  onUpload(e: Event): void;
  onBrowse(): void;
  removeSelectedFiles(index: number): void;
  readonly disableMultiSelect?: boolean;
  readonly acceptedFiles?: string;
  readonly allowedFileExtensions?: string[];
  readonly allowedFileNames?: string[];
  readonly unsupportedFile: boolean;
  readonly is3DEmbbedded?: boolean;
}

const FilesFromComputerComponent: Component<
  IFilesFromComputerComponentProps
> = (props) => {
  onMount(() => {
    defineSolidIconButton();
  });

  const getFileExtensionsText = (): string => {
    if (
      !props.allowedFileExtensions ||
      props.allowedFileExtensions.length === 0
    )
      return '';

    const extensions = props.allowedFileExtensions;
    if (extensions.length === 1) {
      return extensions[0];
    }

    return [extensions.slice(0, -1).join(', '), extensions.slice(-1)[0]].join(
      ' and '
    );
  };

  const getFileNamesText = (): string => {
    if (!props.allowedFileNames || props.allowedFileNames.length === 0)
      return '';

    const names = props.allowedFileNames;
    if (names.length === 1) {
      return names[0];
    }

    return [names.slice(0, -1).join(', '), names.slice(-1)[0]].join(' and ');
  };

  return (
    <>
      <Show when={props.selectedObject.length === 0}>
        <>
          <Show when={!props.disableMultiSelect}>
            {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
            <small
              class="text-meta"
              // eslint-disable-next-line @typescript-eslint/naming-convention
              style={{ 'font-size': '0.75rem', 'font-weight': '400' }}
            >
              {translate('uploadMultiDescription')}
            </small>
          </Show>
          <br />
          <Show
            when={
              props.allowedFileExtensions &&
              props.allowedFileExtensions.length > 0
            }
          >
            <small class="text-meta medium">
              {translate('TC.Common.AttchmentPickerFileTypes')}{' '}
              {getFileExtensionsText()}
            </small>
          </Show>
          <Show
            when={props.allowedFileNames && props.allowedFileNames.length > 0}
          >
            <small class="text-meta medium">
              Allowed file names: {getFileNamesText()}
            </small>
          </Show>
          <div
            class="file-upload-section"
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div
              class={`full-width ${
                !props.is3DEmbbedded ? 'd-none d-md-block' : ''
              }`}
            >
              <div
                class={`droppable ${props.unsupportedFile ? 'error' : ''} ${
                  props.isDragEnter ? 'active' : ''
                }`}
                onDrop={(e) => props.onDrop(e)}
                onDragEnter={(e) => props.onDragEnter(e)}
                onDragOver={(e) => props.onDragOver(e)}
                onDragLeave={() => props.onDragLeave()}
              >
                <i
                  class={`icon-font ${
                    props.unsupportedFile && !props.allowedFileNames
                      ? 'tc-icon-block'
                      : 'tc-icon-file'
                  }`}
                />
                <Show
                  when={props.unsupportedFile && props.allowedFileExtensions}
                  fallback={
                    <Show
                      when={props.unsupportedFile && props.allowedFileNames}
                      fallback={
                        <span class="text-meta mt-1">
                          {translate('TC.File.Upload.DragAndDropFilesHere')}
                        </span>
                      }
                    >
                      <span class="text">
                        {translate('TC.Topics.UploadRelinkError')}
                      </span>
                    </Show>
                  }
                >
                  <span class="text-meta">
                    {translate('TC.Common.AttchmentPickerFileTypes')}{' '}
                    {getFileExtensionsText()}
                  </span>
                </Show>
              </div>
            </div>
            <p
              class={`text-bold pt-1 ${
                !props.is3DEmbbedded ? 'd-none d-md-block' : ''
              }`}
            >
              - {translate('or')} -
            </p>

            <input
              type="file"
              id="fileUpload"
              name="fileUpload"
              class="d-none"
              multiple={!props.disableMultiSelect}
              ref={props.setFileUploadRef}
              onChange={(e) => props.onUpload(e)}
              accept={props.acceptedFiles}
            />
            <button
              class="button secondary mt-2 mb-4"
              onClick={() => props.onBrowse()}
            >
              {translate('TC.Common.Browse')}
            </button>
          </div>
          <small class="text-meta">
            {translate('TC.AttachmentsUploadInfo')}
          </small>
        </>
      </Show>
      <Show when={props.selectedObject.length > 0}>
        <>
          <section class="scrolling-content borderless">
            <div
              class={`full-width flex-0 ${
                !props.is3DEmbbedded ? 'd-none d-md-block' : ''
              }`}
            >
              <div
                id="dropped_file"
                class={`droppable mb-1 ${
                  props.unsupportedFile ? 'error' : ''
                } ${!props.unsupportedFile ? 'bg-white' : ''} ${
                  props.isDragEnter ? 'active' : ''
                }`}
                onDrop={(e) => props.onDrop(e)}
                onDragEnter={(e) => props.onDragEnter(e)}
                onDragOver={(e) => props.onDragOver(e)}
                onDragLeave={() => props.onDragLeave()}
              >
                <i
                  class={`icon-font ${
                    props.unsupportedFile ? 'tc-icon-block' : 'tc-icon-file'
                  }`}
                />
                &nbsp;
                <input
                  type="file"
                  id="fileUpload"
                  name="fileUpload"
                  class="d-none"
                  multiple={!props.disableMultiSelect}
                  ref={props.setFileUploadRef}
                  onChange={(e) => props.onUpload(e)}
                  accept={props.acceptedFiles}
                />
                <Show
                  when={
                    props.unsupportedFile &&
                    props.allowedFileExtensions &&
                    !props.allowedFileNames
                  }
                  fallback={
                    <Show
                      when={props.unsupportedFile && props.allowedFileNames}
                      fallback={
                        <span class="text-meta">
                          {translate('TC.File.Upload.DragAndDropFilesHere')}{' '}
                          {translate('or')}{' '}
                          <a class="pointer" onClick={props.onBrowse}>
                            {translate('browse')}
                          </a>
                        </span>
                      }
                    >
                      <span class="text">
                        {translate('TC.Topics.UploadRelinkError')}
                      </span>
                    </Show>
                  }
                >
                  <span class="text-meta">
                    {translate('TC.Common.AttchmentPickerFileTypes')}{' '}
                    {getFileExtensionsText()}
                  </span>
                </Show>
              </div>
            </div>
            <ul class="list">
              <For each={props.selectedObject}>
                {(fileObject: File, index) => (
                  <li class="list-item group">
                    <div class="group-items bg-white">
                      <div class="flex-0">
                        <i class="icon-font tc-icon-file" />
                      </div>
                      <div class="block text">{fileObject.name}</div>
                      {/* @ts-expect-error - tc-icon-button is a web component defined by defineSolidIconButton */}
                      <tc-icon-button
                        icon="tc-icon-content-close"
                        clicked={() => {
                          props.removeSelectedFiles(index());
                        }}
                        size="icon-small"
                        color="tertiary"
                      />
                    </div>
                  </li>
                )}
              </For>
            </ul>
          </section>
          <small class="text-meta">
            {translate('TC.AttachmentsUploadInfo')}
          </small>
        </>
      </Show>
    </>
  );
};

const FilesFromComputerContainer: Component<IFilesFromComputerProps> = (
  props
) => {
  const [filesFromComputer, setFilesFromComputer] =
    createSignal<IFilesFromComputerState>({
      selectedObject: [],
      type: ITab.FileFromComputer,
      source: 'attachment-picker',
    });
  const [isDragEnter, setIsDragEnter] = createSignal(false);
  const [unsupportedFile, setUnsupportedFile] = createSignal(false);
  const [fileUploadRef, setFileUploadRef] = createSignal<HTMLInputElement>();
  // Reset selected files when tab changes - optimized to prevent unnecessary updates
  createEffect(
    on(
      () => props.tab,
      (currentTab, prevTab) => {
        if (prevTab !== undefined && currentTab !== ITab.FileFromComputer) {
          batch(() => {
            setFilesFromComputer((prev) => ({ ...prev, selectedObject: [] }));
          });
        }
      },
      { defer: true }
    )
  );

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const allowedFiles = (files: File[]): File[] => {
    if (!props.allowedFileExtensions) return files;

    return files.filter((file: File) => {
      const fileExt = getFileExtension(file.name);
      return props.allowedFileExtensions!.some(
        (ext) => ext.toLowerCase() === `.${fileExt}`
      );
    });
  };

  const addFilesFromComputer = (files: File[] | FileList): void => {
    const currentFiles = filesFromComputer().selectedObject;
    const allFiles = currentFiles.concat(Array.from(files));

    // Remove duplicates based on file name
    const distinctFiles = allFiles.filter(
      (file, index, self) =>
        index === self.findIndex((f) => f.name === file.name)
    );

    // Use batch to group state updates and prevent cascading re-renders
    batch(() => {
      const newState = {
        ...filesFromComputer(),
        selectedObject: distinctFiles,
      };
      setFilesFromComputer(newState);
      props.onFilesSubmit({
        selectedObject: distinctFiles,
        type: ITab.FileFromComputer,
        source: 'attachment-picker',
      });
    });
  };

  const onDragEnter = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragEnter(true);
  };

  const onDragOver = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragEnter(true);
  };

  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragEnter(false);

    const files = Array.from(e.dataTransfer?.files || []);

    if (props.allowedFileExtensions) {
      const allowedFilesList = allowedFiles(files);
      if (allowedFilesList.length !== files.length) {
        setUnsupportedFile(true);
      }
      addFilesFromComputer(allowedFilesList);
    } else if (props.allowedFileNames) {
      const allowedFilesList = files.filter((file) =>
        props.allowedFileNames!.includes(file.name)
      );
      if (allowedFilesList.length !== files.length) {
        setUnsupportedFile(true);
      }
      if (allowedFilesList.length > 0) {
        addFilesFromComputer(allowedFilesList);
      }
    } else {
      addFilesFromComputer(files);
    }
  };

  const onUpload = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    if (!props.allowedFileExtensions && !props.allowedFileNames) {
      addFilesFromComputer(files);
    } else if (props.allowedFileExtensions) {
      const allowedFilesList = allowedFiles(filesArray);
      if (allowedFilesList.length !== filesArray.length) {
        setUnsupportedFile(true);
      }
      if (allowedFilesList.length > 0) {
        addFilesFromComputer(allowedFilesList);
      }
    } else if (props.allowedFileNames) {
      const allowedFilesList = filesArray.filter((file) =>
        props.allowedFileNames!.includes(file.name)
      );
      if (allowedFilesList.length !== filesArray.length) {
        setUnsupportedFile(true);
      }
      if (allowedFilesList.length > 0) {
        addFilesFromComputer(allowedFilesList);
      }
    }
  };

  const onBrowse = (): void => {
    const input = fileUploadRef();
    if (input) {
      input.click();
    }
  };

  const removeSelectedFiles = (index: number): void => {
    const currentFiles = [...filesFromComputer().selectedObject];
    currentFiles.splice(index, 1);

    // Use batch to group state updates and prevent cascading re-renders
    batch(() => {
      const newState = { ...filesFromComputer(), selectedObject: currentFiles };
      setFilesFromComputer(newState);

      props.onFilesSubmit({
        selectedObject: currentFiles,
        type: ITab.FileFromComputer,
        source: 'attachment-picker',
      });
    });
  };

  // Create memoized computations to prevent unnecessary re-calculations
  const acceptedFiles = createMemo(() => props.allowedUploadFileExt?.join(','));

  const componentProps = createMemo(() => ({
    selectedObject: filesFromComputer().selectedObject,
    isDragEnter: isDragEnter(),
    onDrop,
    onDragEnter,
    onDragOver,
    onDragLeave: () => setIsDragEnter(false),
    setFileUploadRef,
    onUpload,
    onBrowse,
    removeSelectedFiles,
    disableMultiSelect: props.disableMultiSelect,
    acceptedFiles: acceptedFiles(),
    allowedFileExtensions: props.allowedFileExtensions,
    unsupportedFile: unsupportedFile(),
    is3DEmbbedded: props.is3DEmbbedded,
    allowedFileNames: props.allowedFileNames,
  }));

  return (
    <Show
      when={props.tab === ITab.FileFromComputer && props.isShowFileFromComputer}
    >
      <FilesFromComputerComponent {...componentProps()} />
    </Show>
  );
};

export default FilesFromComputerContainer;
